import React, { useEffect, useState, useCallback } from 'react';
import { User, Briefcase, Phone, Mail, MapPin, Building, Calendar, Users } from 'lucide-react';
import { formatTeamRoleLabel, formatInstallationTrade } from '../utils/teamMemberLabels';
import { teamService } from '../services/teamService';
import { ConfirmationModal } from '../../../components/ui/ConfirmationModal';
import { DetailsWorkspaceModal } from '../../../components/ui';

const fmt = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isAuditNoteLine = (line) => /^\[\d{4}-\d{2}-\d{2}T/.test(String(line).trim());

const splitNotes = (raw) => {
  const lines = String(raw || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const admin = lines.filter((l) => !isAuditNoteLine(l)).join('\n');
  const audit = lines.filter((l) => isAuditNoteLine(l));
  return { adminNotes: admin, auditLines: audit };
};

export const ViewEmployeeModal = ({ employee, onClose, onEmployeeUpdated }) => {
  const [local, setLocal] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (employee) setLocal({ ...employee });
    else setLocal(null);
  }, [employee]);

  const applyUpdate = useCallback(
    async (payload) => {
      if (!employee?._id) return;
      setSaving(true);
      try {
        const res = await teamService.updateEmployee(employee._id, payload);
        const updated = res?.data != null ? res.data : { ...employee, ...payload };
        setLocal((prev) => ({ ...(prev || employee), ...updated }));
        onEmployeeUpdated?.(updated);
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
    },
    [employee, onEmployeeUpdated]
  );

  const handleEnable = () => {
    applyUpdate({ status: 'Active' });
  };

  const handleConfirmDisable = async () => {
    try {
      await applyUpdate({ status: 'Inactive' });
    } finally {
      setConfirmDisable(false);
    }
  };

  const handleAddNote = async (text) => {
    if (!employee?._id) return;
    const cur = local ?? employee;
    let actor = 'User';
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      actor = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.role || 'User';
    } catch {
      /* ignore */
    }
    const line = `[${new Date().toISOString()}] ${actor}: ${text}`;
    const baseNotes = cur.notes;
    const nextNotes = [baseNotes, line].filter(Boolean).join('\n');
    setSavingNote(true);
    try {
      await teamService.updateEmployee(employee._id, { notes: nextNotes });
      setLocal((prev) => ({ ...(prev || employee), notes: nextNotes }));
      onEmployeeUpdated?.({ ...cur, notes: nextNotes });
    } catch (errNote) {
      console.error(errNote);
    } finally {
      setSavingNote(false);
    }
  };

  if (!employee) return null;

  const e = local ?? employee;
  const isInactive = e.status === 'Inactive';
  const roleLabel = formatTeamRoleLabel(e.role);
  const tradeLine =
    e.role === 'field_officer' ? formatInstallationTrade(e.installationTrade) : null;

  const { adminNotes, auditLines } = splitNotes(e.notes);
  
  // Format the audit lines to look like standard events
  const timelineEvents = auditLines.slice(-12);
  const timeline = [
    ...timelineEvents,
    `Profile Created at ${fmt(e.createdAt)}`
  ];

  // Map the team member fields to the exact prop structure!
  const fields = [
    { icon: User, label: "Full Name", value: `${e.firstName} ${e.lastName}` },
    { icon: Users, label: "Employee ID", value: e.employeeId },
    { icon: Mail, label: "Email Address", value: e.email },
    { icon: Phone, label: "Phone Number", value: e.phone },
    { icon: Briefcase, label: "Job Title", value: roleLabel },
    ...(e.role === 'field_officer' ? [{ icon: Briefcase, label: "Role Type", value: tradeLine || '—' }] : []),
    { icon: Calendar, label: "Joining Date", value: fmtDate(e.joiningDate) },
    { icon: MapPin, label: "Work Location", value: e.workLocation },
    // { icon: Building, label: "Department", value: e.department || 'Tower' },
    { icon: Users, label: "Login User ID", value: e.loginId || '—' },
    { icon: Users, label: "Administrative Notes", value: adminNotes || '—' }
  ];

  return (
    <>
      <DetailsWorkspaceModal
        isOpen={!!employee}
        onClose={onClose}
        title="Verve Kitchens"
        subtitle="Team Member Details"
        fields={fields}
        timeline={timeline}
        onAddNote={handleAddNote}
        isInactive={isInactive}
        statusLabel={isInactive ? "Enable Profile" : "Disable Profile"}
        statusColor={isInactive ? "emerald" : "rose"}
        onStatusToggle={isInactive ? handleEnable : () => setConfirmDisable(true)}
        saving={saving}
        savingNote={savingNote}
      />

      <ConfirmationModal
        isOpen={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        onConfirm={handleConfirmDisable}
        title="Disable Member?"
        message="They will be removed from new task assignment. You can enable them again later."
        type="warning"
        confirmLabel="Yes, Disable"
        isLoading={saving}
      />
    </>
  );
};
